import { cn, Tooltip } from "@win-reality/react-ui";
import { ReactNode } from "react";

import { TeamRoster } from "../../types/roster";

/** Whether the user can add players to the roster (unset: editable). */
export const canEditRoster = (roster: TeamRoster) => roster.canEdit !== false;

/** Why players can't be added to the roster. */
const getRosterLockedReason = (roster: TeamRoster) => {
  if (roster.isOfficial) {
    return "This is an official roster. Players can only be added to an organization's own rosters.";
  }
  if (roster.orgId) {
    return "Adding players to this roster needs a staff, staff admin or account owner role in the organization that owns it.";
  }
  return "Players can only be added to your organization's own rosters, and this team's roster is official or belongs to another organization.";
};

type RosterEditTooltipProps = {
  roster: TeamRoster;
  /** Classes for the wrapper, e.g. "w-full" around a full-width button */
  className?: string;
  children: ReactNode;
  /** Literal id for the wrapper from the call site; see TEST_IDS.md */
  "data-testid"?: string;
};

/**
 * Explains a control disabled because the user can't edit the roster. The
 * tooltip sits on a wrapper: disabled buttons don't get hover events.
 */
export const RosterEditTooltip = ({
  roster,
  className,
  children,
  "data-testid": testId,
}: RosterEditTooltipProps) => {
  const isLocked = !canEditRoster(roster);
  return (
    <Tooltip content={getRosterLockedReason(roster)} isDisabled={!isLocked}>
      <span
        data-testid={testId}
        data-status={isLocked ? "locked" : "editable"}
        className={cn("inline-flex", className)}
      >
        {children}
      </span>
    </Tooltip>
  );
};
